import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SupportController, useSupport } from './useSupport';

/** Auth pages have no user to attach a ticket to. */
const HIDDEN_PATHS = ['/signin', '/signup', '/forgot-password', '/set-password'];

const FAB_HIDDEN_KEY = 'support_fab_hidden';

function readFabHidden(): boolean {
  try {
    return localStorage.getItem(FAB_HIDDEN_KEY) === 'true';
  } catch {
    // Private window or blocked site data: show the button, it is the safe default.
    return false;
  }
}

export interface SupportContextValue extends SupportController {
  /** Signed in and on a screen where support makes sense. */
  available: boolean;
  /** The user asked for the floating button to go away (per browser). */
  fabHidden: boolean;
  setFabHidden: (hidden: boolean) => void;
  /** True while some other entry point (the sidebar) is on screen. */
  hasOtherEntry: boolean;
  registerEntry: () => () => void;
}

const SupportContext = createContext<SupportContextValue | null>(null);

/**
 * Holds the one support conversation state for the whole app, so the floating button, the
 * sidebar entry and the panel all drive the same tickets and the same unread count.
 */
export const SupportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const available = isAuthenticated && !HIDDEN_PATHS.includes(pathname);
  const support = useSupport(available);

  const [fabHidden, setFabHiddenState] = useState(readFabHidden);
  const [entryCount, setEntryCount] = useState(0);

  const setFabHidden = useCallback((hidden: boolean) => {
    setFabHiddenState(hidden);
    try {
      localStorage.setItem(FAB_HIDDEN_KEY, String(hidden));
    } catch {
      // The preference just does not survive a reload; nothing else breaks.
    }
  }, []);

  // Each mounted entry point (today only the sidebar) counts itself in, so the floating button
  // knows whether hiding it would leave the user with no way to reach support.
  const registerEntry = useCallback(() => {
    setEntryCount((c) => c + 1);
    return () => setEntryCount((c) => Math.max(0, c - 1));
  }, []);

  const value = useMemo<SupportContextValue>(
    () => ({
      ...support,
      available,
      fabHidden,
      setFabHidden,
      hasOtherEntry: entryCount > 0,
      registerEntry,
    }),
    [support, available, fabHidden, setFabHidden, entryCount, registerEntry],
  );

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
};

export function useSupportPanel(): SupportContextValue {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupportPanel must be used within a SupportProvider');
  return ctx;
}

/** Declare this component as a permanent way into support, e.g. the sidebar entry. */
export function useRegisterSupportEntry(): void {
  const { registerEntry } = useSupportPanel();
  useEffect(() => registerEntry(), [registerEntry]);
}
